import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type FocusEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import BtnTaratic from './BtnTaratic';
import { useResolvedTheme } from './useResolvedTheme';
import './BehaviorInfo.css';

/** Brief help that works with hover, keyboard navigation and touch. */
export default function BehaviorInfo({ label, title, children }: {
  label: string;
  title?: string;
  children: ReactNode;
}) {
  const id = useId();
  const theme = useResolvedTheme();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const focusPanelOnOpen = useRef(false);
  const pinned = useRef(false);
  const hovered = useRef(false);
  const focused = useRef(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);

  const cancelClose = useCallback(() => {
    if (closeTimer.current !== null) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  }, []);

  const close = useCallback((restoreFocus = false) => {
    cancelClose();
    pinned.current = false;
    if (restoreFocus && panelRef.current?.contains(document.activeElement)) {
      anchorRef.current?.querySelector('button')?.focus({ preventScroll: true });
    }
    // Apply dismissal after focus restoration, which may fire our focus handler.
    setOpen(false);
  }, [cancelClose]);

  const leave = () => {
    hovered.current = false;
    cancelClose();
    // Allow the pointer to cross the small space between button and help.
    closeTimer.current = setTimeout(() => {
      if (!pinned.current && !hovered.current && !focused.current) close();
    }, 160);
  };

  const focus = () => {
    cancelClose();
    focused.current = true;
    setOpen(true);
  };

  const blur = (event: FocusEvent<HTMLElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && (anchorRef.current?.contains(next) || panelRef.current?.contains(next))) return;
    focused.current = false;
    close();
  };

  useEffect(() => cancelClose, [cancelClose]);

  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Node && !anchorRef.current?.contains(target) && !panelRef.current?.contains(target)) close();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Consume this Escape so help inside a modal closes before the modal.
      event.preventDefault();
      event.stopPropagation();
      close(true);
    };
    document.addEventListener('pointerdown', outside, true);
    document.addEventListener('keydown', escape, true);
    return () => {
      document.removeEventListener('pointerdown', outside, true);
      document.removeEventListener('keydown', escape, true);
    };
  }, [open, close]);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!open || !anchor || !panel) return;

    const position = () => {
      const bounds = anchor.getBoundingClientRect();
      const viewport = window.visualViewport;
      const viewportWidth = viewport?.width ?? window.innerWidth;
      const viewportHeight = viewport?.height ?? window.innerHeight;
      const viewportLeft = viewport?.offsetLeft ?? 0;
      const viewportTop = viewport?.offsetTop ?? 0;
      const margin = 12;
      const gap = 6;
      const width = Math.max(0, Math.min(352, viewportWidth - margin * 2));
      panel.style.width = `${width}px`;
      const below = viewportTop + viewportHeight - bounds.bottom - gap - margin;
      const above = bounds.top - viewportTop - gap - margin;
      const aboveTrigger = below < Math.min(panel.scrollHeight || 240, 240) && above > below;
      const maxHeight = Math.max(0, Math.min(440, aboveTrigger ? above : below));
      panel.style.maxHeight = `${maxHeight}px`;
      const height = Math.min(panel.scrollHeight, maxHeight);
      const top = aboveTrigger ? bounds.top - gap - height : bounds.bottom + gap;
      Object.assign(panel.style, {
        left: `${Math.max(viewportLeft + margin, Math.min(bounds.right - width, viewportLeft + viewportWidth - width - margin))}px`,
        top: `${Math.max(viewportTop + margin, Math.min(top, viewportTop + viewportHeight - height - margin))}px`,
      });
    };

    position();
    if (focusPanelOnOpen.current) {
      focusPanelOnOpen.current = false;
      panel.focus({ preventScroll: true });
    }
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(position);
    observer?.observe(panel);
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
    window.visualViewport?.addEventListener('resize', position);
    window.visualViewport?.addEventListener('scroll', position);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', position);
      window.removeEventListener('scroll', position, true);
      window.visualViewport?.removeEventListener('resize', position);
      window.visualViewport?.removeEventListener('scroll', position);
    };
  }, [open, children]);

  return (
    <>
      <span
        ref={anchorRef}
        className="behavior-info"
        data-theme={theme}
        onPointerEnter={(event) => {
          if (event.pointerType === 'touch') return;
          cancelClose();
          hovered.current = true;
          setOpen(true);
        }}
        onPointerLeave={leave}
        onFocus={focus}
        onBlur={blur}
      >
        <BtnTaratic
          type="button"
          iconOnly
          className="behavior-info__trigger"
          aria-label={label}
          aria-expanded={open}
          aria-controls={open ? id : undefined}
          aria-describedby={open ? `${id} ${id}-keyboard-hint` : `${id}-keyboard-hint`}
          aria-keyshortcuts="ArrowDown"
          onKeyDown={(event) => {
            if (event.key !== 'ArrowDown') return;
            event.preventDefault();
            cancelClose();
            if (panelRef.current) panelRef.current.focus({ preventScroll: true });
            else {
              focusPanelOnOpen.current = true;
              setOpen(true);
            }
          }}
          onClick={() => {
            if (pinned.current) close();
            else {
              cancelClose();
              pinned.current = true;
              setOpen(true);
            }
          }}
        >
          <span className="behavior-info__icon" aria-hidden="true">i</span>
        </BtnTaratic>
        <span id={`${id}-keyboard-hint`} className="sr-only">Pulsa la flecha abajo para desplazarte por la información. Escape para cerrar.</span>
      </span>
      {open && createPortal(
        <div
          ref={panelRef}
          id={id}
          role="tooltip"
          className="behavior-info__panel"
          data-theme={theme}
          tabIndex={-1}
          onPointerEnter={(event) => {
            if (event.pointerType === 'touch') return;
            cancelClose();
            hovered.current = true;
          }}
          onPointerLeave={leave}
          onFocus={focus}
          onBlur={blur}
          onKeyDown={(event) => {
            if (event.key !== 'Tab') return;
            // Resume the page's normal tab order from the trigger, not the portal.
            anchorRef.current?.querySelector('button')?.focus({ preventScroll: true });
            if (event.shiftKey) event.preventDefault();
            else close();
          }}
        >
          {title && <strong className="behavior-info__title">{title}</strong>}
          <div className="behavior-info__content">{children}</div>
        </div>,
        // Native dialogs occupy the top layer; a body portal would sit behind them.
        anchorRef.current?.closest('dialog[open]') ?? document.body,
      )}
    </>
  );
}
